import { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getImageUrl, getRealFileName } from "src/Common/common";
import AttachmentCard from "./Components/attachment/AttachmentCard";
import AttachmentDeleteModal from "./Components/attachment/AttachmentDeleteModal";
import AttachmentEditModal from "./Components/attachment/AttachmentEditModal";
import AttachmentImageSlider from "./Components/attachment/AttachmentImageSlider";
import { handleError } from "src/Common/function";
import WOService from "src/Services/API/wo.service";
import { useDispatch } from "react-redux";
import { setWONumber } from "src/Redux/Slices/HeaderSlice";
import { toast } from "react-toastify";
import { Oval } from "react-loader-spinner";
import { downloadPdf, downloadImage } from "src/Common/function";
import { getUploadUrl } from "src/Common/common";

const WoAttachments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [attachments, setAttachments] = useState([]);
  const [isSliderShown, setIsSliderShown] = useState(false);
  const [isEditModalShown, setIsEditModalShown] = useState(false);
  const [isDeleteModalShown, setIsDeleteModalShown] = useState(false);
  const [activeAttachmentIndex, setActiveAttachmentIndex] = useState(0);

  useEffect(() => {
    getAttachments();
  }, []);

  const getAttachments = () => {
    WOService.getWorkOrderById(location.state?.woId)
      .then((response) => {
        if (response.status == 200) {
          setAttachments(response.data.attachments);
          dispatch(setWONumber(String(response.data.number)));
        }
      })
      .catch((error) => handleError(error));
  };

  const normalizeTags = (tags) => {
    if (!tags) return [];
    if (Array.isArray(tags)) {
      return tags
        .map((tag) => {
          if (!tag) return null;
          if (typeof tag === "string") {
            const name = tag.trim();
            return name ? { name } : null;
          }
          const name = (tag?.name || tag?.label || "").trim();
          if (!name) return null;
          return tag?.id ? { id: tag.id, name } : { name };
        })
        .filter(Boolean);
    }
    if (typeof tags === "string") {
      return tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean)
        .map((name) => ({ name }));
    }
    return [];
  };

  const uiAttachments = useMemo(() => {
    return attachments.map((attachment) => {
      const fileName = attachment?.fileName || "";
      const description = attachment?.description?.trim() || null;
      const tags = normalizeTags(attachment?.tags);
      const createdBy =
        attachment?.uploadedBy
          ? attachment?.uploadedBy?.name
          : attachment?.uploadedByCustomerUser?.customer
            ?.companyName || "";
      const createdAt = attachment?.createdAt || null;
      const isPdf = fileName.toLowerCase().endsWith(".pdf");

      return {
        id: attachment?.id || fileName,
        fileName,
        displayName: getRealFileName(fileName) || fileName || "Attachment",
        description,
        tags,
        createdBy,
        createdAt,
        isPdf,
      };
    });
  }, [attachments]);

  const selectedAttachment = uiAttachments[activeAttachmentIndex] || null;

  const handleSaveAttachment = ({ description, tags }) => {
    console.log("🚀 ~ handleSaveAttachment ~ tags:", tags)
    setIsEditModalShown(false);
    let data = {

    }
    if (description.trim() !== "") {
      data.description = description.trim();
    }
    if (tags.length > 0) {
      data.tags = tags;
    }
    // check if data is empty
    if (Object.keys(data).length === 0) {
      return;
    }

    WOService.updateAttachment({
      id: selectedAttachment.id,
      data,
    }).then((response) => {
      getAttachments()
    }).catch((error) => {
      console.log(error);
    });
    // setAttachments((prevAttachments) =>
    //   prevAttachments.map((attachment, index) => {
    //     if (index !== activeAttachmentIndex) return attachment;
    //     return {
    //       ...attachment,
    //       description,
    //       tags,
    //     };
    //   }),
    // );
  };

  const handleDeleteAttachment = () => {
    WOService.deleteAttachment(location.state?.woId, selectedAttachment.id, 1)
      .then((response) => {
        console.log("🚀 ~ handleDeleteAttachment ~ response:", response)

        getAttachments();
        toast("Attachment deleted successfully");
      })
      .catch((error) => {
        handleError(error);
      });

    setIsDeleteModalShown(false);
    setActiveAttachmentIndex(0);

  };



  return (
    <div>
      <div className="w-full h-[56px] flex items-center justify-between ">
        <div className="flex items-center cursor-pointer" onClick={() => navigate(-1)}>
          <img src={getImageUrl("/images/common/back_arrow.png")} className="w-[24px]" />
          <span className="font-OpenSansRegular text-[14px] font-[700] text-subTitleColor">back</span>
        </div>
      </div>
      {uiAttachments.length > 0 ? (
        <div className="w-full flex flex-wrap gap-5">
          {uiAttachments.map((attachment, index) => (
            <AttachmentCard
              key={attachment.id}
              attachment={attachment}
              onPreview={() => {
                setActiveAttachmentIndex(index);
                setIsSliderShown(true);
              }}
              onEdit={() => {
                setActiveAttachmentIndex(index);
                setIsEditModalShown(true);
              }}
              onDelete={() => {
                setActiveAttachmentIndex(index);
                setIsDeleteModalShown(true);
              }}
              onDownload={() => {
                if (attachment.isPdf) {
                  downloadPdf(getUploadUrl(attachment.fileName), getRealFileName(attachment.fileName));
                } else {
                  downloadImage(getUploadUrl(attachment.fileName), getRealFileName(attachment.fileName));
                }
              }}
            />
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <Oval
            height={50}
            width={50}
            strokeWidth={2}
            color="#010D3D"
            secondaryColor="#80869E"
            ariaLabel="oval-loading"
            visible={true}
          />
        </div>
      )}

      <AttachmentImageSlider
        shown={isSliderShown}
        attachments={uiAttachments}
        activeIndex={activeAttachmentIndex}
        onCancel={() => setIsSliderShown(false)}
      />
      <AttachmentEditModal
        shown={isEditModalShown}
        attachment={selectedAttachment}
        onCancel={() => setIsEditModalShown(false)}
        onSave={handleSaveAttachment}
      />
      <AttachmentDeleteModal
        shown={isDeleteModalShown}
        attachment={selectedAttachment}
        onCancel={() => setIsDeleteModalShown(false)}
        onConfirm={handleDeleteAttachment}
      />
    </div>
  );
};

export default WoAttachments;